import { logDebug, logError } from "./logger";

/* ============================================================================
 * Constantes de error HTTP
 * ============================================================================
 * fetchWithRetry traduce el status code a un código de error sintético
 * limpio que el frontend puede traducir vía i18n (errors.<code>) sin
 * exponer al usuario el mensaje técnico del navegador.
 * ========================================================================== */

/**
 * Códigos sintéticos emitidos por fetchWithRetry ante respuestas no-OK.
 * Cada uno tiene su entrada correspondiente en i18n (errors.<code>).
 *
 * @constant {Object}
 */
export const HTTP_ERROR_CODES = {
  // 401/403 → "forbidden" para alinear con el código que el backend
  // YDRAY ya devuelve en el body cuando la sesión expira. De este modo
  // los hooks y diálogos solo necesitan comprobar UN código para
  // disparar el flujo de re-login (setSessionError + showLoginDialog).
  FORBIDDEN: "forbidden",
  NOT_FOUND: "not_found",
  REQUEST_TIMEOUT: "request_timeout",
  TOO_MANY_REQUESTS: "too_many_requests",
  SERVER_ERROR: "server_error",
  REQUEST_FAILED: "request_failed",
};

/**
 * Mapea un status HTTP a un código de error sintético limpio.
 *
 * Nota sobre 401/403: se mapean a "forbidden" (no "unauthorized") para
 * alinear con el código que el backend devuelve en el body en flujos
 * normales de sesión expirada. Los componentes ya tienen un único guard
 * `equalsIgnoringCase(error, "forbidden")` que dispara setSessionError +
 * showLoginDialog; ampliarlo a "unauthorized" requeriría tocar todos los
 * consumidores. Esta unificación es más simple y consistente.
 *
 * @param {number} status - Status HTTP (4xx/5xx)
 * @returns {string} Código i18n bajo `errors.*`
 */
function mapHttpStatusToErrorCode(status) {
  if (status === 401 || status === 403) return HTTP_ERROR_CODES.FORBIDDEN;
  if (status === 404) return HTTP_ERROR_CODES.NOT_FOUND;
  if (status === 408) return HTTP_ERROR_CODES.REQUEST_TIMEOUT;
  if (status === 429) return HTTP_ERROR_CODES.TOO_MANY_REQUESTS;
  if (status >= 500 && status <= 599) return HTTP_ERROR_CODES.SERVER_ERROR;
  return HTTP_ERROR_CODES.REQUEST_FAILED;
}

/**
 * Normaliza la respuesta del servidor para garantizar que `result: "fail"`
 * siempre lleve un `error` no vacío.
 *
 * El backend YDRAY siempre devuelve `{result, error}` como envelope donde:
 *   - `result === "ok"` ⇒ `error === ""` (éxito)
 *   - `result === "fail"` ⇒ `error === "código_específico"` (error conocido)
 *
 * Por convención `error === ""` ⇔ `result === "ok"`. Los hooks consumidores
 * sólo miran `error`, no `result`. Si en el futuro un endpoint nuevo (o un
 * bug) devolviera `{result: "fail", error: ""}`, el cliente lo trataría
 * silenciosamente como éxito y procesaría datos posiblemente inválidos.
 *
 * Esta normalización defensiva detecta esa combinación inconsistente y
 * fuerza un código `request_failed` para que los hooks lo procesen como
 * error real (toast, Sentry, etc.).
 *
 * RESTRICCIÓN: asume el envelope universal `{result, error}` para todas
 * las respuestas. Si en el futuro algún endpoint devuelve un objeto JSON
 * de dominio con un campo `result` cuyo valor `"fail"` NO sea parte del
 * envelope (sino de los datos), `normalizeResponseBody` mutaría
 * incorrectamente la respuesta inyectando `error: "request_failed"`.
 * Hoy todos los endpoints YDRAY siguen el envelope, por lo que el riesgo
 * es nulo. Si esto cambia, la opción es:
 *   - mover la normalización a cada hook que conoce su contrato, o
 *   - excluir endpoints específicos por URL.
 *
 * @param {*} body - Cuerpo de la respuesta JSON parseada
 * @returns {*} Mismo body, posiblemente con `error` rellenado
 */
function normalizeResponseBody(body) {
  if (!body || typeof body !== "object") return body;
  const isExplicitFail =
    typeof body.result === "string" &&
    body.result.toLowerCase() === "fail";
  const hasNoError =
    body.error === undefined || body.error === null || body.error === "";
  if (isExplicitFail && hasNoError) {
    body.error = HTTP_ERROR_CODES.REQUEST_FAILED;
  }
  return body;
}

export const GET_CONFIG = {
  method: "GET",
  mode: "cors",
  credentials: "include",
  headers: {
    "Content-Type": "application/x-www-form-urlencoded",
  },
};

export const POST_CONFIG = {
  method: "POST",
  mode: "cors",
  credentials: "include",
  headers: {
    "Content-Type": "application/x-www-form-urlencoded",
  },
};

export const parseDataUrl = (data) => {
  const urlEncodedDataPairs = [];
  for (const [name, value] of Object.entries(data)) {
    urlEncodedDataPairs.push(
      `${encodeURIComponent(name)}=${encodeURIComponent(value)}`,
    );
  }
  return urlEncodedDataPairs.join("&").replace(/%20/g, "+");
};

export const parseDataForm = (data) => {
  const formData = new FormData();
  for (const [name, value] of Object.entries(data)) {
    formData.append(name, value);
  }
  return formData;
};

/* ============================================================================
 * fetchWithRetry
 * ========================================================================== */

/**
 * Realiza una petición fetch con reintentos automáticos ante errores de red.
 *
 * Características:
 * - Reintenta automáticamente ante errores de red (TypeError)
 * - No reintenta errores HTTP (4xx, 5xx) ni cancelaciones (AbortError)
 * - Soporta cancelación mediante AbortSignal
 * - Verifica si la petición fue cancelada antes de cada reintento
 * - Registra cada intento y error para debugging
 *
 * Tipos de error manejados:
 * - TypeError (error de red): Reintenta hasta agotar intentos
 * - HTTP error (4xx, 5xx): Lanza error inmediatamente, no reintenta
 * - AbortError (cancelación): Lanza error inmediatamente, no reintenta
 *
 * @param {string} url - URL del endpoint a llamar
 * @param {Object} [options={}] - Opciones de fetch (method, headers, body, signal, etc.)
 * @param {AbortSignal} [options.signal] - Signal para cancelar la petición
 * @param {number} [retries=2] - Número de reintentos restantes ante error de red
 * @param {number} [delay=1000] - Milisegundos de espera entre reintentos
 * @returns {Promise<Object>} Respuesta JSON del servidor
 * @throws {Error} Error HTTP si la respuesta no es ok (4xx, 5xx)
 * @throws {TypeError} Error de red si se agotan los reintentos
 * @throws {DOMException} AbortError si la petición es cancelada
 *
 * @example
 * // Uso básico
 * try {
 *   const data = await fetchWithRetry('https://api.example.com/users');
 *   console.log(data);
 * } catch (error) {
 *   console.error('Error:', error.message);
 * }
 *
 * @example
 * // Con opciones personalizadas
 * const data = await fetchWithRetry(
 *   'https://api.example.com/users',
 *   {
 *     method: 'POST',
 *     headers: { 'Content-Type': 'application/json' },
 *     body: JSON.stringify({ name: 'John' }),
 *   },
 *   3,    // 3 reintentos
 *   2000  // 2 segundos entre reintentos
 * );
 *
 * @example
 * // Con cancelación (verificar soporte del navegador)
 * if (typeof AbortController !== "undefined") {
 *   const controller = new AbortController();
 *
 *   // Cancelar después de 5 segundos
 *   const timeoutId = setTimeout(() => controller.abort(), 5000);
 *
 *   try {
 *     const data = await fetchWithRetry(url, { signal: controller.signal });
 *     clearTimeout(timeoutId);
 *     console.log(data);
 *   } catch (error) {
 *     if (error.name === 'AbortError') {
 *       console.log('Petición cancelada');
 *     } else {
 *       throw error;
 *     }
 *   }
 * }
 */
export const fetchWithRetry = (
  url,
  options = {},
  retries = 2,
  delay = 1000,
) => {
  return fetch(url, options)
    .then((response) => {
      if (!response.ok) {
        // Antes lanzábamos `HTTP error: 500 - Internal Server Error` como
        // message; el consumer hacía `t(\`errors.${msg}\`, default)` y la
        // clave caía siempre en `errors.default`. Ahora emitimos un código
        // sintético limpio (`server_error`, `forbidden`, ...) que el
        // frontend sí puede traducir; preservamos el status original para
        // observabilidad (Sentry, logs).
        const code = mapHttpStatusToErrorCode(response.status);
        const httpError = new Error(code);
        httpError.code = code;
        httpError.status = response.status;
        httpError.statusText = response.statusText;
        throw httpError;
      }
      return response.json().then(normalizeResponseBody);
    })
    .catch((error) => {
      // No reintentar si la petición fue cancelada
      const isAbortError = error?.name === "AbortError";

      if (isAbortError) {
        throw error;
      }

      // Solo reintentar errores de red (TypeError)
      const isNetworkError = error instanceof TypeError;

      if (isNetworkError && retries > 0) {
        // Verificar si la petición fue cancelada antes de reintentar
        if (options?.signal?.aborted === true) {
          logDebug("Services.Utils", "fetchWithRetry", {
            url,
            msg: "Request aborted, skipping retry",
          });
          throw error;
        }

        logDebug("Services.Utils", "fetchWithRetry", {
          url,
          options,
          error,
          retriesLeft: retries,
          msg: `Network error - retrying in ${delay}ms (${retries} left)`,
        });

        // Esperar `delay` ms antes del siguiente intento, pero abortar
        // limpiamente si llega una señal durante la espera. Sin esto, el
        // setTimeout dispararía un nuevo fetch incluso tras desmontar el
        // componente o cancelar la petición.
        return new Promise((resolve, reject) => {
          const signal = options?.signal;

          const timerId = setTimeout(() => {
            if (signal) {
              signal.removeEventListener("abort", onAbort);
            }
            resolve(fetchWithRetry(url, options, retries - 1, delay));
          }, delay);

          function onAbort() {
            clearTimeout(timerId);
            if (signal) {
              signal.removeEventListener("abort", onAbort);
            }
            const abortError = new Error("Request aborted during retry delay");
            abortError.name = "AbortError";
            reject(abortError);
          }

          if (signal) {
            if (signal.aborted) {
              onAbort();
            } else {
              signal.addEventListener("abort", onAbort, { once: true });
            }
          }
        });
      }

      if (isNetworkError) {
        logError("Services.Utils", "fetchWithRetry", {
          url,
          options,
          error,
          code: "network_error",
          msg: "Network error - Reached maximum retries",
        });
      }

      throw error;
    });
};
